import { defaultLocale, normalizeLocale } from '../i18n/index.js';
import { normalizeAgentOnboardingUrl } from './agent-onboarding.js';
export { supportedLocales } from '../i18n/index.js';

const optionNames = ['target', 'locale', 'theme', 'currentProduct', 'connectedApps', 'hiddenProducts', 'agentOnboardingUrl', 'style', 'callbacks'];
const callbackNames = ['onReady', 'onError', 'onLocaleChange', 'onThemeChange', 'onOpen', 'onClose', 'onAction', 'onTrack'];

function assertObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`[PositiveNav] ${name} must be an object.`);
  }
}

function assertKeys(value, allowed, name) {
  for (const key of Object.keys(value)) {
    if (!allowed.includes(key)) throw new Error(`[PositiveNav] Unknown option in ${name}: ${key}`);
  }
}

function copyIds(value, name) {
  if (!Array.isArray(value)) throw new TypeError(`[PositiveNav] ${name} must be an array.`);
  return Object.freeze([...new Set(value)]);
}

/** Partial updates replace lists and merge style and callbacks by key. */
export function normalizeOptions(partial = {}, previous, documentLocale = '') {
  assertObject(partial, 'options');
  assertKeys(partial, optionNames, 'options');
  if (partial.style !== undefined) {
    assertObject(partial.style, 'style');
    assertKeys(partial.style, ['position', 'zIndex'], 'style');
  }
  if (partial.callbacks !== undefined) {
    assertObject(partial.callbacks, 'callbacks');
    assertKeys(partial.callbacks, callbackNames, 'callbacks');
  }

  const merged = { ...previous, ...partial };
  if (merged.currentProduct !== undefined && typeof merged.currentProduct !== 'string') {
    throw new TypeError('[PositiveNav] currentProduct must be a product ID string.');
  }
  const theme = merged.theme ?? 'light';
  if (!['light', 'dark'].includes(theme)) throw new Error('[PositiveNav] theme must be light or dark.');
  const style = { ...previous?.style, ...partial.style };
  style.position ??= 'sticky';
  if (!['fixed', 'sticky', 'relative'].includes(style.position)) {
    throw new Error('[PositiveNav] style.position must be fixed, sticky or relative.');
  }
  if (style.zIndex !== undefined && !Number.isSafeInteger(style.zIndex)) {
    throw new TypeError('[PositiveNav] style.zIndex must be an integer.');
  }
  const callbacks = { ...previous?.callbacks, ...partial.callbacks };
  for (const [name, callback] of Object.entries(callbacks)) {
    if (callback != null && typeof callback !== 'function') {
      throw new TypeError(`[PositiveNav] callbacks.${name} must be a function or null.`);
    }
  }

  return Object.freeze({
    target: merged.target,
    locale: normalizeLocale(merged.locale === undefined ? documentLocale || defaultLocale: merged.locale),
    theme,
    currentProduct: merged.currentProduct,
    agentOnboardingUrl: normalizeAgentOnboardingUrl(merged.agentOnboardingUrl),
    connectedApps: copyIds(merged.connectedApps === undefined ? []: merged.connectedApps, 'connectedApps'),
    hiddenProducts: copyIds(merged.hiddenProducts === undefined ? []: merged.hiddenProducts, 'hiddenProducts'),
    style: Object.freeze(style),
    callbacks: Object.freeze(callbacks),
  });
}
